import genAI from './geminiClient';

/**
 * Generates social media post content using Gemini 2.5 Flash
 * @param {Object} options - Post generation options
 * @param {string} options.topic - Main topic or theme for the post
 * @param {string} options.tone - Tone of the post (professional, casual, humorous, etc.)
 * @param {string} options.platform - Target social media platform
 * @param {number} options.maxLength - Maximum character length
 * @param {string[]} options.keywords - Keywords to include in the post
 * @returns {Promise<Object>} Generated post content with metadata
 */
export async function generatePost(options) {
  const { topic, tone = 'professional', platform = 'general', maxLength = 280, keywords = [] } = options;

  try {
    const keywordText = keywords?.length > 0 ? ` Include these keywords naturally: ${keywords?.join(', ')}.` : '';
    
    const prompt = `Create an engaging ${platform} social media post about "${topic}".
    
Requirements:
- Tone: ${tone}
- Maximum length: ${maxLength} characters
- Make it attention-grabbing and authentic${keywordText}
- Include relevant hashtags if appropriate for ${platform}
- Focus on value and engagement

Generate only the post content, no explanations.`;

    const model = genAI?.getGenerativeModel({ 
      model: 'gemini-2.5-flash',
      generationConfig: {
        temperature: 0.7,
        topP: 0.8,
        topK: 32,
        maxOutputTokens: 500
      }
    });

    const result = await model?.generateContent(prompt);
    const response = await result?.response;
    const content = response?.text()?.trim();

    return {
      content,
      metadata: {
        topic,
        tone,
        platform,
        generatedAt: new Date()?.toISOString(),
        characterCount: content?.length,
        provider: 'gemini'
      }
    };
  } catch (error) {
    console.error('Error generating post with Gemini:', error);
    throw new Error('Failed to generate post content. Please try again.');
  }
}

/**
 * Generates multiple post variations for A/B testing
 * @param {Object} options - Post generation options
 * @param {number} variations - Number of variations to generate
 * @returns {Promise<Array>} Array of generated post variations
 */
export async function generatePostVariations(options, variations = 3) {
  try {
    const posts = await Promise.all(
      Array.from({ length: variations }, () => generatePost(options))
    );
    return posts;
  } catch (error) {
    console.error('Error generating post variations with Gemini:', error);
    throw new Error('Failed to generate post variations. Please try again.');
  }
}

/**
 * Analyzes post content and suggests improvements using Gemini
 * @param {string} content - Post content to analyze
 * @param {string} platform - Target platform
 * @returns {Promise<Object>} Analysis and suggestions
 */
export async function analyzePost(content, platform = 'general') {
  try {
    const prompt = `Analyze this ${platform} post and provide specific, actionable suggestions to improve engagement:

"${content}"

Provide your analysis in the following JSON format:
{
  "strengths": ["strength 1", "strength 2", ...],
  "improvements": ["improvement 1", "improvement 2", ...],
  "suggestions": ["suggestion 1", "suggestion 2", ...],
  "overallScore": <number between 0 and 10>
}`;

    const model = genAI?.getGenerativeModel({ 
      model: 'gemini-2.5-flash',
      generationConfig: {
        temperature: 0.7,
        topP: 0.8,
        topK: 32,
      }
    });

    const result = await model?.generateContent(prompt);
    const response = await result?.response;
    const text = response?.text()?.trim();
    
    // Extract JSON from response (handles markdown code blocks)
    const jsonMatch = text?.match(/\{[\s\S]*\}/);
    if (!jsonMatch) {
      throw new Error('Invalid response format from Gemini');
    }
    
    return JSON.parse(jsonMatch?.[0]);
  } catch (error) {
    console.error('Error analyzing post with Gemini:', error);
    throw new Error('Failed to analyze post content. Please try again.');
  }
}